/**
 * The pounce aim, drawn: the beads and the ring that show where the oracle
 * says this charge lands.
 *
 * The DOM twin of packages/engine/src/pounce.js, split from it at that file's
 * own "---- the drawing ----" line by §1.2's standing rule, exactly as
 * burrs.js is: the oracle flies a real Player and is engine, this draws its
 * answer and is client.  The design record for what the preview promises --
 * and the one rule that it must never lie -- lives in the engine half's
 * header; what is here is only how the answer is made visible.
 *
 * RING_OUTER below must stay equal to the engine half's BLOCKED_LOSS: a
 * deflection the ring itself would cover is one nobody can see, which is
 * exactly where an arc starts reading as obstructed.  The original file said
 * "RING_OUTER below" while the number sat inline in the geometry call; naming
 * it is what lets contracts.mjs hold the two sides together now that they
 * live in different packages.
 */
import * as THREE from "three";
import { PounceOracle } from "@catworld/engine";

/** Re-exported so the client's own callers need one import, not two. */
export { PounceOracle };

const RING_OUTER = 0.235;

// ---- the drawing -----------------------------------------------------------

const ACCENT = 0xe2a73c;    // the HUD's own "on" colour, so the arc and the
                            // charge meter under it are visibly one control
const BLOCKED = 0xd4614a;   // the flight clips something -- still a leap, but
                            // not the one the aim was pointed at
const DOT_GAP = 0.12;       // m between beads, measured along the arc
// Beads are small on purpose.  The first cut drew them at 0.13 m, which at
// the 2 m the camera actually sits behind the cat is a third of the screen
// height per bead: the near half of the arc fused into one orange smear and
// the apex became a row of dinner plates hanging over the street.  At 0.045
// they read as a line at every range the camera can be at.
const DOT_SIZE = 0.045;
const MAX_DOTS = 60;
const FADE = 0.09;          // s of ease on and off, matched to #charge's .12s

/**
 * The arc itself: a bead line out of the cat and a ring where it comes down.
 *
 * Beads rather than a solid line because a tube through a hedge is unreadable
 * and a line has no scale -- evenly spaced dots are their own ruler, so "two
 * more dots than last time" is a distance the player can read at a glance
 * without any numbers on screen.  Additive so a dot that dims IS a dot that
 * fades, which is what lets the tail of the arc trail off into the world
 * instead of stopping at a hard edge, and depth-tested so an arc that passes
 * behind the fence goes behind the fence.
 */
export class PounceArc {
  constructor() {
    this.object3D = new THREE.Group();
    this.object3D.frustumCulled = false;   // one small object that is always
                                           // near the camera; culling it costs
                                           // more than drawing it
    this.shown = 0;                        // 0..1 fade, eased toward `want`

    const pos = new Float32Array(MAX_DOTS * 3);
    const col = new Float32Array(MAX_DOTS * 3);
    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.BufferAttribute(pos, 3));
    geo.setAttribute("color", new THREE.BufferAttribute(col, 3));
    geo.setDrawRange(0, 0);
    this.dotPos = pos;
    this.dotCol = col;
    this.dots = new THREE.Points(geo, new THREE.PointsMaterial({
      map: dotTexture(),
      size: DOT_SIZE,
      sizeAttenuation: true,
      vertexColors: true,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    }));
    this.dots.frustumCulled = false;
    this.object3D.add(this.dots);

    // The landing ring, laid flat on whatever the paws will touch.  Cat-sized
    // (the collision capsule is 0.19 m of radius) so it reads as a footprint
    // rather than a map pin, and drawn without depth *write* so the arc's
    // last beads sitting on top of it do not punch holes in it.
    this.ring = new THREE.Mesh(
      new THREE.RingGeometry(0.17, RING_OUTER, 40),
      new THREE.MeshBasicMaterial({
        color: ACCENT, transparent: true, depthWrite: false,
        side: THREE.DoubleSide,
      }));
    this.ring.rotation.x = -Math.PI / 2;
    this.ring.frustumCulled = false;
    this.object3D.add(this.ring);

    // A pip in the middle of the ring: at a full pounce's range the ring is
    // small on screen, and a solid centre is what makes it findable against
    // grass without growing the ring into something that lies about the
    // precision on offer.
    this.pip = new THREE.Mesh(
      new THREE.CircleGeometry(0.045, 20),
      new THREE.MeshBasicMaterial({
        color: ACCENT, transparent: true, depthWrite: false,
        side: THREE.DoubleSide,
      }));
    this.pip.rotation.x = -Math.PI / 2;
    this.pip.frustumCulled = false;
    this.object3D.add(this.pip);
  }

  /**
   * One frame.  Call after player.update, with the same oracle every time.
   *
   * Predicts only while the arc is actually being shown or faded out, so an
   * ordinary tap-jump -- which is `charging` for one frame and never
   * `gathering` -- costs nothing at all, and a cat walking down the street
   * costs one boolean.
   */
  /** @param {number} dt @param {any} player @param {any} oracle */
  update(dt, player, oracle) {
    const want = player.gathering ? 1 : 0;
    // Ease rather than snap: the crouch fades in over POSE_DELAY and the arc
    // arriving hard in front of it reads as a bug in the pose.
    const k = 1 - Math.exp(-dt / FADE);
    this.shown += (want - this.shown) * k;
    if (want) this.shown = Math.max(this.shown, 0.02);
    if (this.shown < 0.01) {
      this.shown = 0;
      this.object3D.visible = false;
      return;
    }
    this.object3D.visible = true;
    // The arc keeps being flown through the fade-out, so a released pounce
    // shows its own arc shrinking away under the cat that is flying it,
    // rather than freezing a stale line in mid-air for a tenth of a second.
    this._draw(oracle.predict(player), this.shown);
  }

  /** @param {any} p @param {number} alpha */
  _draw(p, alpha) {
    const tint = p.blocked ? BLOCKED : ACCENT;
    _colour.setHex(tint);

    // Walk the flown path and drop a bead every DOT_GAP of arc length, so the
    // spacing is a distance on the ground and not a rate of the integrator --
    // at even time steps the beads bunch at the apex, which draws the eye to
    // the top of the arc when the whole question is about the far end of it.
    let n = 0, carry = 0;
    for (let i = 1; i <= p.steps && n < MAX_DOTS; i++) {
      const a = p.path[i - 1], b = p.path[i];
      const seg = a.distanceTo(b);
      if (seg < 1e-6) continue;
      for (let d = DOT_GAP - carry; d <= seg && n < MAX_DOTS; d += DOT_GAP) {
        const t = d / seg;
        this.dotPos[n * 3] = a.x + (b.x - a.x) * t;
        this.dotPos[n * 3 + 1] = a.y + (b.y - a.y) * t;
        this.dotPos[n * 3 + 2] = a.z + (b.z - a.z) * t;
        n++;
      }
      carry = (carry + seg) % DOT_GAP;
    }

    // Brightest at the cat, trailing off toward the ring, which is what makes
    // the line read as leaving the cat rather than as an object lying in the
    // grass.  Additive, so this ramp IS the fade -- see the class note.
    for (let i = 0; i < n; i++) {
      const f = alpha * (1 - 0.62 * (i / Math.max(1, n - 1)));
      this.dotCol[i * 3] = _colour.r * f;
      this.dotCol[i * 3 + 1] = _colour.g * f;
      this.dotCol[i * 3 + 2] = _colour.b * f;
    }
    this.dots.geometry.setDrawRange(0, n);
    this.dots.geometry.attributes.position.needsUpdate = true;
    this.dots.geometry.attributes.color.needsUpdate = true;

    // The ring sits a centimetre proud of the surface: coplanar with it, it
    // z-fights the paving across the whole street.
    const y = p.land.y + 0.012;
    this.ring.position.set(p.land.x, y, p.land.z);
    this.pip.position.set(p.land.x, y, p.land.z);
    this.ring.material.color.setHex(tint);
    this.pip.material.color.setHex(tint);
    // A landing the flight never reached (150 steps of falling -- off the map,
    // or a drop taller than the level) has no ring to offer: the beads still
    // show the arc, and nothing claims to know where it ends.
    const solid = p.landed ? alpha : 0;
    this.ring.material.opacity = solid;
    this.pip.material.opacity = solid * 0.9;
    this.ring.visible = this.pip.visible = solid > 0.01;
  }
}

const _colour = new THREE.Color();

/**
 * One soft bead, drawn once.  A radial falloff rather than a hard disc: at
 * additive blending a hard-edged dot has a visible rim where it crosses
 * anything bright, and the whole arc turns into a string of stickers.
 */
function dotTexture() {
  // Headless (the aim gate flies arcs with no renderer at all): no canvas to
  // draw on, and a null map on a PointsMaterial is a square dot nobody sees.
  if (typeof document === "undefined") return null;
  const size = 64;
  const c = document.createElement("canvas");
  c.width = c.height = size;
  const g = /** @type {CanvasRenderingContext2D} */ (c.getContext("2d"));
  const grad = g.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  grad.addColorStop(0, "rgba(255,255,255,1)");
  grad.addColorStop(0.45, "rgba(255,255,255,0.72)");
  grad.addColorStop(1, "rgba(255,255,255,0)");
  g.fillStyle = grad;
  g.fillRect(0, 0, size, size);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}
